import React, { useState, useRef, useEffect } from 'react';
import {
    ActionsBar,
    UploadButton,
    FilterSection,
    StyledTableContainer,
    TableHeader,
    StatusCell,
    StatusBadge,
    ActionButton,
    DeleteModalBox
} from './DocumentsTab.styles';
import {
    Table,
    TableBody,
    TableHead,
    TableRow,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Paper,
    TableCell,
    Skeleton,
    Modal,
    Typography,
    Button,
    Box,
    Alert,
    CircularProgress
} from '@mui/material';
import { Add, Description, PictureAsPdf, Search, Delete, Visibility, ErrorOutline } from '@mui/icons-material';
import { formatBytes, formatDateTime } from '@utils/helpers';
import { documentEmbeddingApi } from '@api/api';

export default function DocumentsTab({ documents, isLoading, refreshDocuments, onViewDocument }) {
    const [filterType, setFilterType] = useState('all');
    const [filterStatus, setFilterStatus] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [docToDeleteId, setDocToDeleteId] = useState(null);
    const [confirmDeleteText, setConfirmDeleteText] = useState('');
    const [isDeleting, setIsDeleting] = useState(false);

    const fileInputRef = useRef(null);

    const handleImportClick = () => {
        fileInputRef.current.click();
    };

    const handleFileChange = (event) => {
        const files = Array.from(event.target.files);
        if (files.length > 0) {
            handleFileUpload(files[0]);
        }
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleFileUpload = async (selectedFile) => {
        try {
            const result = await documentEmbeddingApi.uploadDocument(selectedFile);
            if (result.ok) {
                refreshDocuments();
            } else {
                let errorMsg = `Upload failed with status: ${result.status}`;
                try {
                    const errorData = await result.json();
                    errorMsg = errorData.message || errorMsg;
                } catch (e) { /* Ignore if response not JSON */ }
                throw new Error(errorMsg);
            }
        } catch (error) {
            console.error("An error occurred during upload:", error);
            alert(`Upload failed: ${error.message}`);
        } finally {
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const handleOpenDeleteModal = (docId) => {
        setDocToDeleteId(docId);
        setConfirmDeleteText('');
        setIsDeleting(false);
        setOpenDeleteModal(true);
    };

    const handleCloseDeleteModal = () => {
        setOpenDeleteModal(false);
        setDocToDeleteId(null);
        setConfirmDeleteText('');
        setIsDeleting(false);
    };

    const handleConfirmDelete = async () => {
        if (confirmDeleteText === 'DELETE' && docToDeleteId) {
            setIsDeleting(true);
            try {
                await documentEmbeddingApi.deleteDocument(docToDeleteId);
                refreshDocuments();
                handleCloseDeleteModal();
            } catch (error) {
                console.error('Error deleting document:', error);
                alert(`Failed to delete document: ${error.message}`);
                setIsDeleting(false);
            }
        }
    };

    const handleFilterTypeChange = (event) => setFilterType(event.target.value);
    const handleFilterStatusChange = (event) => setFilterStatus(event.target.value);
    const handleSearchTermChange = (event) => setSearchTerm(event.target.value);

    const filteredDocs = React.useMemo(() => {
        if (!Array.isArray(documents)) {
            return [];
        }
        return documents.filter(doc => {
            if (!doc || typeof doc.name !== 'string' || typeof doc.file_type !== 'string' || typeof doc.status !== 'string') {
                return false;
            }
            if (filterType !== 'all' && doc.file_type.toLowerCase() !== filterType) {
                return false;
            }
            if (filterStatus !== 'all' && doc.status.toUpperCase() !== filterStatus.toUpperCase()) {
                return false;
            }
            if (searchTerm && !doc.name.toLowerCase().includes(searchTerm.toLowerCase())) {
                return false;
            }
            return true;
        });
    }, [documents, filterType, filterStatus, searchTerm]);

    if (isLoading) {
        return (
            <>
                <ActionsBar>
                    <Skeleton variant="rectangular" width={190} height={36} sx={{ borderRadius: '4px' }} />
                    <FilterSection>
                        <Skeleton variant="rectangular" width={180} height={40} sx={{ borderRadius: '4px' }} />
                        <Skeleton variant="rectangular" width={120} height={40} sx={{ borderRadius: '4px' }} />
                        <Skeleton variant="rectangular" width={140} height={40} sx={{ borderRadius: '4px' }} />
                    </FilterSection>
                </ActionsBar>

                <StyledTableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableHeader width="30%"><Skeleton variant="text" sx={{ fontSize: '1rem' }} /></TableHeader>
                                <TableHeader width="10%"><Skeleton variant="text" sx={{ fontSize: '1rem' }} /></TableHeader>
                                <TableHeader width="10%"><Skeleton variant="text" sx={{ fontSize: '1rem' }} /></TableHeader>
                                <TableHeader width="15%"><Skeleton variant="text" sx={{ fontSize: '1rem' }} /></TableHeader>
                                <TableHeader width="15%"><Skeleton variant="text" sx={{ fontSize: '1rem' }} /></TableHeader>
                                <TableHeader width="20%" style={{ textAlign: 'right' }}><Skeleton variant="text" sx={{ fontSize: '1rem' }} /></TableHeader>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {[...Array(5)].map((_, index) => (
                                <TableRow key={index}>
                                    <TableCell><Skeleton variant="text" sx={{ fontSize: '1rem' }} /></TableCell>
                                    <TableCell><Skeleton variant="text" sx={{ fontSize: '1rem' }} /></TableCell>
                                    <TableCell><Skeleton variant="text" sx={{ fontSize: '1rem' }} /></TableCell>
                                    <TableCell><Skeleton variant="text" sx={{ fontSize: '1rem' }} /></TableCell>
                                    <TableCell><Skeleton variant="text" width="60%" sx={{ fontSize: '1rem' }} /></TableCell>
                                    <TableCell align="right">
                                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '6px' }}>
                                            <Skeleton variant="circular" width={32} height={32} />
                                            <Skeleton variant="circular" width={32} height={32} />
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </StyledTableContainer>
                <Skeleton variant="text" width={200} height={20} sx={{ fontSize: '0.9rem', marginTop: '5px' }}/>
            </>
        );
    }

    return (
    <>
        <ActionsBar>
            <UploadButton
                variant="contained"
                startIcon={<Add/>}
                onClick={handleImportClick}
            >
                Upload Document
            </UploadButton>
            <input
                data-testid="file-selector"
                ref={fileInputRef}
                type="file"
                accept=".pdf,.doc,.docx,.txt"
                style={{ display: "none" }}
                onChange={handleFileChange}
            />

            <FilterSection>
                <TextField
                    placeholder="Search documents..."
                    size="small"
                    value={searchTerm}
                    onChange={handleSearchTermChange}
                    InputProps={{
                        startAdornment: (
                            <Search />
                        ),
                    }}
                />

                <FormControl size="small" style={{ minWidth: '120px' }}>
                    <InputLabel id="filter-type-label">Type</InputLabel>
                    <Select
                        labelId="filter-type-label"
                        value={filterType}
                        label="Type"
                        onChange={handleFilterTypeChange}
                    >
                        <MenuItem value="all">All Types</MenuItem>
                        <MenuItem value="pdf">PDF</MenuItem>
                        <MenuItem value="docx">DOCX</MenuItem>
                        <MenuItem value="doc">DOC</MenuItem>
                        <MenuItem value="txt">TXT</MenuItem>
                    </Select>
                </FormControl>

                <FormControl size="small" style={{ minWidth: '140px' }}>
                    <InputLabel id="filter-status-label">Status</InputLabel>
                    <Select
                        labelId="filter-status-label"
                        value={filterStatus}
                        label="Status"
                        onChange={handleFilterStatusChange}
                    >
                        <MenuItem value="all">All Status</MenuItem>
                        <MenuItem value="SUCCESS">Completed</MenuItem>
                        <MenuItem value="PROCESSING">Processing</MenuItem>
                        <MenuItem value="FAILED">Failed</MenuItem>
                        <MenuItem value="PENDING">Pending</MenuItem>
                    </Select>
                </FormControl>
            </FilterSection>
        </ActionsBar>

        {!isLoading && !Array.isArray(documents) && (
            <Alert severity="error" sx={{ mt: 2 }}>Could not load documents list.</Alert>
        )}

        {Array.isArray(documents) && (
            <StyledTableContainer component={Paper}>
                <Table stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableHeader width="35%">Document Name</TableHeader>
                            <TableHeader width="10%">Type</TableHeader>
                            <TableHeader width="10%">Size</TableHeader>
                            <TableHeader width="15%">Upload Date</TableHeader>
                            <TableHeader width="15%">Status</TableHeader>
                            <TableHeader width="15%" align="right">Actions</TableHeader>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filteredDocs.length === 0 && (
                            <TableRow>
                                <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                                    <Typography color="textSecondary">
                                        No documents match the current filters.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                        {filteredDocs.map((doc) => (
                            <TableRow key={doc.id} hover>
                                <TableCell component="th" scope="row">
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {doc.file_type?.toLowerCase() === 'pdf' ? <PictureAsPdf color="action" /> : <Description color="action" />}
                                        <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                                            {doc.name || `(ID: ${doc.id})`}
                                        </Typography>
                                    </Box>
                                </TableCell>
                                <TableCell>{doc.file_type?.toUpperCase() || 'N/A'}</TableCell>
                                <TableCell>{formatBytes(doc.file_size)}</TableCell>
                                <TableCell>{formatDateTime(doc.created_at)}</TableCell>
                                <StatusCell status={doc.status}>
                                    <StatusBadge
                                        label={doc.status || 'Unknown'}
                                        status={doc.status}
                                        size="small"
                                    />
                                    {doc.status === 'FAILED' && (
                                        <ErrorOutline fontSize="small" color="error" sx={{ ml: 0.5, verticalAlign: 'middle' }} titleAccess="Processing failed"/>
                                    )}
                                </StatusCell>
                                <TableCell align="right">
                                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                                        <ActionButton title="View Document" onClick={() => onViewDocument(doc)} size="small">
                                            <Visibility fontSize="small" />
                                        </ActionButton>
                                        <ActionButton title="Delete Document" onClick={() => handleOpenDeleteModal(doc.id)} size="small" color="error">
                                            <Delete fontSize="small" />
                                        </ActionButton>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </StyledTableContainer>
        )}

        {Array.isArray(documents) && (
            <Typography variant="caption" sx={{ display: 'block', textAlign: 'right', mt: 1, color: 'text.secondary' }}>
                Showing {filteredDocs.length} of {documents.length} documents
            </Typography>
        )}

        <Modal
            open={openDeleteModal}
            onClose={handleCloseDeleteModal}
            aria-labelledby="delete-confirmation-modal-title"
        >
            <DeleteModalBox>
                <Typography id="delete-confirmation-modal-title" variant="h6" component="h2">
                    Confirm Deletion
                </Typography>
                <Typography sx={{ mt: 2, mb: 2 }}>
                     Are you sure you want to delete the document "{documents?.find(d => d.id === docToDeleteId)?.name || docToDeleteId}"?
                     To confirm, please type "DELETE" below. This action cannot be undone.
                </Typography>
                <TextField
                    margin="normal"
                    required
                    fullWidth
                    id="confirm-delete"
                    label='Type "DELETE" to confirm'
                    value={confirmDeleteText}
                    onChange={(e) => setConfirmDeleteText(e.target.value)}
                    autoFocus
                    error={confirmDeleteText !== '' && confirmDeleteText !== 'DELETE'}
                    helperText={confirmDeleteText !== '' && confirmDeleteText !== 'DELETE' ? 'Please type DELETE exactly.' : ''}
                    disabled={isDeleting}
                />
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                    <Button onClick={handleCloseDeleteModal} disabled={isDeleting}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleConfirmDelete}
                        disabled={confirmDeleteText !== 'DELETE' || isDeleting}
                        startIcon={isDeleting ? <CircularProgress size={20} color="inherit" /> : null}
                    >
                        {isDeleting ? 'Deleting...' : 'Confirm Delete'}
                    </Button>
                </Box>
            </DeleteModalBox>
        </Modal>
    </>
    )
}